import { createSignal, type Accessor } from 'solid-js'

/**
 * The classified result of a purchase orchestration. Adapters never throw;
 * they catch their own failures and return one of these.
 */
export type PurchaseOutcome =
  | { kind: 'cancelled' }
  | { kind: 'committed'; productId: number }
  | { kind: 'confirmation_pending' }
  | { kind: 'connection' }
  | { kind: 'delivery'; orderId: number }
  | { kind: 'insufficient' }
  | { code: string; kind: 'rejected' }

/**
 * One purchase orchestration behind the flow's seam. Besides executing the
 * command, an adapter declares the two policies that follow the operation's
 * commitment boundary rather than the product (see GLOSSARY.md: Shop
 * purchase flow).
 */
export interface PurchaseAdapter {
  /**
   * What the connection dialog's OK does. A soft purchase with an unknown
   * outcome closes Shop to prevent a duplicate command (ADR 0018); an IAP
   * connection failure happens before native payment begins, so returning
   * to where the purchase started for a retry is safe — the confirmation
   * (ADR 0016), or for a flow with none (a starter-pack purchase, ADR 0044)
   * the surface beneath it.
   */
  connectionDismissal: 'close-shop' | 'return-to-start'
  execute: () => Promise<PurchaseOutcome>
  /**
   * How the in-flight command presents. A soft purchase locks its
   * confirmation dialog (ADR 0018 forbids the processing overlay there); an IAP
   * replaces it with the non-dismissible processing overlay (ADR 0016);
   * a free unlock has no confirmation, so the surface it started from
   * presents it inline (GLOSSARY.md: Free product).
   */
  pendingPresentation: 'inline' | 'locked-confirmation' | 'processing-overlay'
}

export type PurchaseFlowPhase =
  | 'confirmation'
  | 'confirming'
  | 'connection'
  | 'delivery'
  | 'error'
  | 'insufficient'
  | 'processing'
  | 'submitting'
  | 'success'

export interface PurchaseFlowMachine {
  confirm: () => Promise<void>
  deliveryOrderId: Accessor<number>
  errorCode: Accessor<string>
  pending: Accessor<boolean>
  phase: Accessor<PurchaseFlowPhase>
  returnToConfirmation: () => void
}

export function createPurchaseFlowMachine(input: {
  adapter: () => PurchaseAdapter
  onCommitted?: (productId: number) => void
}): PurchaseFlowMachine {
  const [deliveryOrderId, setDeliveryOrderId] = createSignal(0)
  const [errorCode, setErrorCode] = createSignal('')
  const [pending, setPending] = createSignal(false)
  const [phase, setPhase] = createSignal<PurchaseFlowPhase>('confirmation')

  const confirm = async () => {
    if (pending()) return
    const adapter = input.adapter()
    setPending(true)
    if (adapter.pendingPresentation === 'processing-overlay') {
      setPhase('processing')
    } else if (adapter.pendingPresentation === 'inline') {
      setPhase('submitting')
    }

    let outcome: PurchaseOutcome
    try {
      outcome = await adapter.execute()
    } catch (error) {
      // Adapters classify their own failures; a rejection here is a
      // contract breach, surfaced like an unclassified rejection.
      console.error('[shop] purchase adapter rejected', error)
      outcome = {
        code: error instanceof Error ? error.message : String(error),
        kind: 'rejected',
      }
    }

    if (outcome.kind === 'committed') {
      input.onCommitted?.(outcome.productId)
      setPhase('success')
      setPending(false)
      return
    }

    setPending(false)
    switch (outcome.kind) {
      case 'cancelled':
        setPhase('confirmation')
        return
      case 'confirmation_pending':
        setPhase('confirming')
        return
      case 'connection':
        setPhase('connection')
        return
      case 'delivery':
        setDeliveryOrderId(outcome.orderId)
        setPhase('delivery')
        return
      case 'insufficient':
        setPhase('insufficient')
        return
      case 'rejected':
        setErrorCode(outcome.code)
        setPhase('error')
        return
    }
  }

  return {
    confirm,
    deliveryOrderId,
    errorCode,
    pending,
    phase,
    returnToConfirmation: () => {
      setPending(false)
      setPhase('confirmation')
    },
  }
}
